import { AxiosError } from 'axios';
import userManagerClient from 'src/api/clients/userManagerClient';
import type { OpenIdResponse, IUser } from 'src/types/responses/loginResponse';
import { systemId } from 'src/app.config';
import setAuthorizationToken from 'src/api/setAuthorizationToken';
import { clearCookies } from 'src/utils/cookiesHelper';
import { loginRedirect } from 'src/utils/loginRedirect';
import type { AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import { SystemClaimPermission } from 'src/types/claims/claimsApp';

const PERMISSIONS_KEY = `metbus_permissions_${systemId}`;
const PERMISSIONS_TOKEN_KEY = `permission_token_${systemId}`;
const envDomain = import.meta.env.VITE_COOKIE_DOMAIN;

export async function GetAuthorization() {
  try {
    const response = await requestTokenExchange();

    if (response.status === 200) {
      const permissionToken = response.data.access_token;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const decodedToken = jwtDecode<any>(permissionToken);

      sessionStorage.setItem(PERMISSIONS_TOKEN_KEY, permissionToken);

      let parsedClaims: SystemClaimPermission = {} as SystemClaimPermission;

      parsedClaims =
        typeof decodedToken.claims === 'string'
          ? JSON.parse(decodedToken.claims)
          : decodedToken.claims;

      setAuthorizationToken();

      const userPayload = {
        userName: decodedToken.username,
        name: decodedToken.name,
        email: decodedToken.email,
        rut: decodedToken.rut,
        rol: decodedToken.rol,
        profileName: decodedToken.profileName,
        claims: parsedClaims,
      } as IUser;

      return {
        response: response.status,
        user: userPayload,
      };
    }
    return { response: response.status, user: null };
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as AxiosError<any>;

    if (err.response?.status === 403) {
      return {
        response: 403,
        user: null,
      };
    }

    if (err.response?.status === 401) {
      const errorData = err.response.data;

      // Clear cookies .metbus.cl, clear storage permissions and redirect to login page
      if (errorData?.requiresLogin) {
        clearCookies();
        sessionStorage.removeItem(PERMISSIONS_KEY);
        loginRedirect();
        return { response: err.response?.status, user: null };
      }
    }
    throw err;
  }
}

export const requestTokenExchange = () => {
  const authToken = document.cookie
    .split('; ')
    .find((row) => row.startsWith('Metbus.AuthToken='))
    ?.split('=')[1];
  if (!authToken) {
    throw new Error('No auth token found');
  }
  const payload = new URLSearchParams();
  payload.append(
    'grant_type',
    'urn:ietf:params:oauth:grant-type:token-exchange'
  );
  payload.append('subject_token', authToken);
  payload.append(
    'subject_token_type',
    'urn:ietf:params:oauth:token-type:access_token'
  );
  payload.append('system_id', systemId);

  return userManagerClient.post<OpenIdResponse>('/api/Connect/Token', payload, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true,
  });
};

// Here function that calls refresh token and updates cookies
export async function refreshToken() {
  try {
    // get refresh token from cookie
    const refreshToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('Metbus.RefreshToken='))
      ?.split('=')[1];
    if (!refreshToken) {
      throw new Error('No refresh token found');
    }
    // set new global cookies with new access token and refresh token
    const response = await resquestRefreshToken(refreshToken);
    const { access_token, refresh_token } = response.data;
    const cookieDomain = envDomain ? `domain=${envDomain}; ` : '';
    const isSecure = window.location.protocol === 'https:' ? 'Secure; ' : '';
    document.cookie = `Metbus.AuthToken=${access_token}; path=/; ${cookieDomain}${isSecure}SameSite=Lax`;
    document.cookie = `Metbus.RefreshToken=${refresh_token}; path=/; ${cookieDomain}${isSecure}SameSite=Lax`;
    // exchange new access token for permission token and store it in session storage
    const exchangeResponse = await requestTokenExchange();
    const systemAccessToken = exchangeResponse?.data?.access_token;
    sessionStorage.setItem(PERMISSIONS_TOKEN_KEY, systemAccessToken);
    return true;
  } catch {
    return false;
  }
}

// Here must be the refresh token, change name of file couse now is athorization and authenticate
async function resquestRefreshToken(refreshToken: string) {
  const payload = new URLSearchParams();
  payload.append('grant_type', 'refresh_token');
  payload.append('refresh_token', refreshToken);
  payload.append('scope', 'offline_access');
  return userManagerClient.post<OpenIdResponse>('/api/Connect/Token', payload, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true,
  });
}

export interface ChangePasswordProps {
  userName: string;
  currentPassword: string;
  newPassword: string;
}

export interface ChangeEmailProps {
  UserName: string;
  NewEmail: string;
  Rut: string;
}

export async function ChangePassword(
  changeProps: ChangePasswordProps
): Promise<{ ok: boolean; message: string }> {
  try {
    const response = await userManagerClient.put<string>(
      '/api/User/ChangePassword',
      changeProps
    );
    switch (response.status) {
      case 200:
        return {
          ok: true,
          message: 'Se ha modificado correctamente la contraseña.',
        };
      case 400:
        return { ok: false, message: 'Usuario incorrecto.' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.status === 400)
      return { ok: false, message: 'Usuario incorrecta.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar la contraseña.',
    };
  }
}

export async function ChangeEmail(
  changeProps: ChangeEmailProps
): Promise<{ ok: boolean; message: string; profileName?: string }> {
  try {
    const responseUser = await userManagerClient.put<{
      message: string;
      profileName: string;
    }>('/api/User/ChangeEmail', changeProps);

    switch (responseUser.status) {
      case 200:
        return {
          ok: true,
          message: responseUser.data.message,
          profileName: responseUser.data.profileName,
        };
      case 400:
        return { ok: false, message: 'Usuario incorrecto.' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.status === 400)
      return { ok: false, message: 'Usuario incorrecto.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar el correo.',
    };
  }
}

export const resetPassword = (userName: string): Promise<AxiosResponse<void>> =>
  userManagerClient.put(
    '/api/User/ResetPasswordUser',
    {},
    {
      params: {
        userName,
      },
    }
  );
